﻿'use strict';

/**
 * Eventifies an object (built with inspiration from smokesignals)
 * @param {object} obj the object to be eventified
 * @returns the object
 */
let eventify = (obj) => {
    // Previously eventified?
    if (obj.on && obj.emit) {
        return obj;
    }

    obj._events_handlers = Object.create(null);

    obj.clearEvents = clearEvents;
    obj.emitOnce    = emitOnce;
    obj.emitAsync   = emitAsync;
    obj.emit        = emit;
    obj.once        = once;
    obj.off         = off;

    obj.deEventify = deEventify;

    /**
     * Adds a listener for an event
     * @param {string} eventName the name of the event
     * @param {*} handler
     * @returns {object} the object
     */
    obj.on = function (eventName, handler) {
        if (!handler) {
            return obj;
        }

        let handlerList = obj._events_handlers[eventName];

        if (!handlerList) {
            handlerList = obj._events_handlers[eventName] = [];
        }

        handlerList.push(handler);

        return {
            unsubscribe: () => {
                obj.off?.(eventName, handler);
                return obj;
            },
            eventName,
            on: obj.on.bind(obj)
        }
    }

    return obj;
}

/**
 * Adds a listener for an event where the callbackhandler is only called once
 * @param {string} eventName the name of the event
 * @param {*} handler
 * @returns {object} the object
 */
function once (eventName, handler) {
    let obj = this;
    let subscription = obj.on (eventName, (eventData) => {
            subscription.unsubscribe();
            handler.call (obj, eventData);

            subscription = null;
            handler      = null;
        });

    return subscription;
}

/**
 * Removes a listener for an event
 * @param {string} eventName the name of the event
 * @param {function} handler the callback function to remove
 * @returns {object} the object
 */
function off (eventName, handler) {
    let obj = this;
    let handlerList = obj._events_handlers[eventName];

    if (!(handlerList && handler)) {
        return obj;
    }

    // Remove all added events for this handler
    let index = handlerList.length;

    while (--index >= 0) {
        if (handlerList[index] === handler) {
            handlerList.splice(index, 1);
        }
    }

    return obj;
}

/**
 * Emits an event only once
 * @param {string} eventName the name of the event
 * @param {*} eventData
 * @returns {object} the object
 */
function emitOnce (eventName, eventData) {
    let obj = this;
    let handlerList = obj._events_handlers[eventName];

    if (!handlerList) {
        handlerList = obj._events_handlers[eventName] = [];
    }

    emit.call(obj, eventName, eventData);

    handlerList._events_oneTimeEventHasFired = true;

    return obj;
}

/**
 * Emits an event asynchrounosly
 * @param {string} eventName the name of the event
 * @param {*} eventData
 * @returns {object} the object
 */
function emitAsync(eventName, eventData) {
    let obj = this;

    setTimeout(() => {
        obj.emit(eventName, eventData);
    }, 0);

    return obj;
}

/**
 * Emits an event
 * @param {string} eventName the name of the event
 * @param {*} eventData
 * @returns {object} the object
 */
function emit (eventName, eventData) {
    let obj = this;
    let handlerList = obj._events_handlers[eventName];

    if (!(handlerList?.length)) {
        return obj;
    }

    if (handlerList._events_oneTimeEventHasFired) {
        return obj;
    }

    let data = typeof eventData === 'function' ? eventData() : eventData;

    // Copy the array to make sure code inside events that might unregister etc.
    // does not prevent all the current attached handlers from beeing called.
    handlerList = handlerList.slice();

    for (let i=0, length=handlerList.length; i<length; i++) {
        handlerList[i]?.call (obj, data);
    }

    return obj;
}

/**
 * Removes every trace of events on the object
 * @returns {object} the object
 */
function deEventify () {
    let obj = this;

    obj.clearEvents?.();

    obj.on                = noop;
    obj.set               = noop;
    obj.get               = noop;
    obj.off               = noop;
    obj.once              = noop;
    obj.emit              = noop;
    obj.emitAsync         = noop;
    obj.emitOnce          = noop;
    obj.clearEvents       = noop;
    obj._events_handlers  = null;

    return obj;
}

/**
 *
 * @returns {object} the object
 */
function noop() {
    return this;
}

/**
 *
 * @returns {object} the object
 */
function clearEvents () {
    let obj = this;

    obj._events_handlers && Object.keys(obj._events_handlers).forEach(key => {
        if (obj._events_handlers[key]) {
            obj._events_handlers[key].length = 0;
            obj._events_handlers[key] = null;
        }
    });

    obj._events_handlers = Object.create(null);

    return obj;
}

export { eventify };
